/**
 * Countdown Timer Section
 * Vanilla JS — no dependencies.
 * Supports multiple instances on the same page and re-initializes
 * automatically inside the Shopify theme editor.
 *
 * Two schedule modes (set via data-schedule-mode on the root element):
 *   - "single": counts down to one fixed date/time (data-end-date),
 *     then stops and shows the ended message, same as before.
 *   - "daily": counts down to a time of day (data-daily-end-time,
 *     "HH:MM") that recurs every day in the visitor's local time.
 *     data-daily-reset-behavior controls what happens once that time
 *     passes for the day:
 *       - "ended_until_midnight" (default): shows the ended message
 *         for the rest of the day, then automatically starts counting
 *         down to tomorrow's occurrence once local midnight passes.
 *       - "continuous": immediately starts counting down to tomorrow's
 *         occurrence, never showing the ended message.
 */
(function () {
  'use strict';

  var INTERVAL_MS = 1000;
  var timers = {}; // rootId -> interval id, so re-init doesn't stack intervals

  function parseEndDate(raw) {
    if (!raw) return null;

    var value = raw.trim();

    // Accept "YYYY-MM-DD HH:MM" (space) or "YYYY-MM-DDTHH:MM" (already ISO-like).
    if (value.indexOf('T') === -1 && value.indexOf(' ') !== -1) {
      value = value.replace(' ', 'T');
    }

    // Add seconds if only HH:MM was provided.
    var hasSeconds = /T\d{2}:\d{2}:\d{2}/.test(value);
    if (!hasSeconds && /T\d{2}:\d{2}$/.test(value)) {
      value += ':00';
    }

    var date = new Date(value);

    if (isNaN(date.getTime())) {
      // Fall back to letting the browser parse the raw string as-is.
      date = new Date(raw);
    }

    return isNaN(date.getTime()) ? null : date;
  }

  function parseTimeOfDay(raw) {
    if (!raw) return null;

    var match = raw.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;

    var hours = parseInt(match[1], 10);
    var minutes = parseInt(match[2], 10);

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

    return { hours: hours, minutes: minutes };
  }

  // Builds "today at HH:MM" relative to `now`, in local time.
  function todayAt(now, timeOfDay) {
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      timeOfDay.hours,
      timeOfDay.minutes,
      0,
      0
    );
  }

  function pad(number) {
    return String(number).padStart(2, '0');
  }

  function updateDisplay(root, remainingMs) {
    var totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));

    var days = Math.floor(totalSeconds / 86400);
    var hours = Math.floor((totalSeconds % 86400) / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;

    var daysEl = root.querySelector('[data-countdown-days]');
    var hoursEl = root.querySelector('[data-countdown-hours]');
    var minutesEl = root.querySelector('[data-countdown-minutes]');
    var secondsEl = root.querySelector('[data-countdown-seconds]');

    if (daysEl) daysEl.textContent = pad(days);
    if (hoursEl) hoursEl.textContent = pad(hours);
    if (minutesEl) minutesEl.textContent = pad(minutes);
    if (secondsEl) secondsEl.textContent = pad(seconds);

    return totalSeconds;
  }

  function stopTimer(rootId) {
    if (timers[rootId]) {
      clearInterval(timers[rootId]);
      delete timers[rootId];
    }
  }

  function setEndedState(root, ended) {
    var activeEl = root.querySelector('[data-countdown-active]');
    var endedEl = root.querySelector('[data-countdown-ended-message]');

    if (activeEl) activeEl.hidden = ended;
    if (endedEl) endedEl.hidden = !ended;

    if (ended) {
      root.setAttribute('data-countdown-ended', 'true');
    } else {
      root.removeAttribute('data-countdown-ended');
    }
  }

  function initSingleMode(root, rootId) {
    var endDate = parseEndDate(root.getAttribute('data-end-date'));

    if (!endDate) {
      // No valid end date configured — treat as not-yet-ended, show zeros.
      setEndedState(root, false);
      updateDisplay(root, 0);
      return;
    }

    function tick() {
      var remaining = endDate.getTime() - Date.now();

      if (remaining <= 0) {
        updateDisplay(root, 0);
        setEndedState(root, true);
        stopTimer(rootId);
        return;
      }

      setEndedState(root, false);
      updateDisplay(root, remaining);
    }

    tick();

    // Only keep ticking if the countdown hasn't already ended.
    if (endDate.getTime() - Date.now() > 0) {
      timers[rootId] = setInterval(tick, INTERVAL_MS);
    }
  }

  function initDailyMode(root, rootId) {
    var timeOfDay = parseTimeOfDay(root.getAttribute('data-daily-end-time'));
    var resetBehavior = root.getAttribute('data-daily-reset-behavior') || 'ended_until_midnight';

    if (!timeOfDay) {
      // No valid daily time configured — treat as not-yet-ended, show zeros.
      setEndedState(root, false);
      updateDisplay(root, 0);
      return;
    }

    function tick() {
      var now = new Date();
      var target = todayAt(now, timeOfDay);
      var remaining = target.getTime() - now.getTime();

      if (remaining > 0) {
        // Still counting down to today's occurrence.
        setEndedState(root, false);
        updateDisplay(root, remaining);
        return;
      }

      if (resetBehavior === 'continuous') {
        // Jump straight to tomorrow's occurrence, no ended state shown.
        target.setDate(target.getDate() + 1);
        remaining = target.getTime() - now.getTime();
        setEndedState(root, false);
        updateDisplay(root, remaining);
        return;
      }

      // "ended_until_midnight": show the ended message for the rest of
      // today. Once local midnight passes, `now`'s date advances, so
      // `target` (today at HH:MM) is back in the future and this branch
      // stops firing automatically — no extra date math needed here.
      updateDisplay(root, 0);
      setEndedState(root, true);
    }

    // Daily mode never permanently stops — it keeps ticking indefinitely
    // so it can roll over at midnight and on every subsequent day.
    tick();
    timers[rootId] = setInterval(tick, INTERVAL_MS);
  }

  function initCountdown(root) {
    if (!root || !(root instanceof HTMLElement)) return;

    var rootId = root.id;
    var scheduleMode = root.getAttribute('data-schedule-mode') || 'single';

    // Always clear any existing interval for this instance before starting a new one
    // (important for theme editor "input" events firing repeatedly).
    stopTimer(rootId);

    if (scheduleMode === 'daily') {
      initDailyMode(root, rootId);
    } else {
      initSingleMode(root, rootId);
    }
  }

  function initAll(context) {
    var scope = context || document;
    var roots = scope.querySelectorAll('[data-countdown-timer]');
    roots.forEach(function (root) {
      initCountdown(root);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  // Shopify theme editor support: re-init on section load/select,
  // and stop the interval when the section is removed or deselected.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-countdown-timer]');
    if (root) initCountdown(root);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-countdown-timer]');
    if (root) stopTimer(root.id);
  });

  document.addEventListener('shopify:section:select', function (event) {
    var root = event.target.querySelector('[data-countdown-timer]');
    if (root) initCountdown(root);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest('[data-countdown-timer]');
    if (root) initCountdown(root);
  });
})();
